import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskedLine, FadeUp } from "@/components/motion";
import { Sparkle } from "@/components/Icons";
import { Float, Reader } from "@/components/Mascots";

const CHAPTERS = [
  {
    num: "CH.I",
    title: "Le bold n'est pas une option.",
    text: "On ne fait pas de sites timides. Chaque pixel est une décision, chaque interaction une intention. Si ça ne provoque rien, ça ne sort pas.",
  },
  {
    num: "CH.II",
    title: "Le digital est un terrain de jeu.",
    text: "On expérimente, on découpe, on anime, on casse les grilles. Le web est notre matière première — et on la travaille comme des artisans obsédés.",
  },
  {
    num: "CH.III",
    title: "Premium jusque dans le code.",
    text: "Derrière l'audace visuelle : des architectures propres, des performances mesurées, un code qu'on signerait deux fois. Le wow ne suffit pas, il doit durer.",
  },
];

const Chapter = ({ ch, index, progress }) => {
  const drift = useTransform(progress, [0, 1], index % 2 === 0 ? [60, -60] : [-60, 60]);
  return (
    <div
      className="border-t border-flytab-line py-10 md:py-14 grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-start"
      data-testid={`manifesto-chapter-${index + 1}`}
    >
      <motion.div style={{ x: drift }} className="md:col-span-4 lg:col-span-3">
        <span className="font-display font-black uppercase text-stroke-green text-6xl md:text-8xl lg:text-[7rem] leading-none whitespace-nowrap">
          {ch.num}
        </span>
      </motion.div>

      <div className="md:col-span-8 lg:col-span-9">
        <h3 className="font-display font-extrabold uppercase tracking-tight leading-[1.02] text-3xl md:text-5xl lg:text-6xl mb-6">
          <MaskedLine delay={0.05}>{ch.title}</MaskedLine>
        </h3>
        <FadeUp delay={0.2} className="max-w-2xl">
          <p className="font-mono text-sm md:text-lg leading-relaxed text-neutral-400">{ch.text}</p>
        </FadeUp>
      </div>
    </div>
  );
};

export default function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });

  return (
    <section id="manifesto" ref={ref} className="bg-flytab-black text-flytab-cream px-4 md:px-10 pt-20 md:pt-28 pb-14 md:pb-20 overflow-x-clip" data-testid="manifesto-section">
      <h2 className="sr-only">Le manifeste FLYTAB</h2>
      <div className="flex items-center gap-4 mb-10 md:mb-14">
        <Sparkle className="w-8 h-8 md:w-10 md:h-10" stroke="none" strokeWidth={0} />
        <FadeUp>
          <span className="font-mono text-xs md:text-sm uppercase tracking-[0.3em] text-flytab-green">(Le manifeste)</span>
        </FadeUp>
        <Float className="ml-auto hidden md:block" amp={10} dur={6}>
          <Reader className="w-28 lg:w-36" />
        </Float>
      </div>

      <div>
        {CHAPTERS.map((ch, i) => (
          <Chapter key={ch.num} ch={ch} index={i} progress={scrollYProgress} />
        ))}
      </div>
    </section>
  );
}
